import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { fmtUSDate } from "../lib/date";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import {
  FileText, Download, Link2, Copy, Trash2, Eye, Calendar, Lock, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Tear Sheet dialog for a single workspace. Exposes every feature the
 * /api/workspaces/{id}/tear-sheet* backend already ships — section toggles,
 * PDF download, password-protected share links with expiry, view-count
 * history, and link revocation. Surfaces nothing about the PDF's internal
 * layout (that's the backend's job); this is purely a delivery console.
 *
 * Props:
 *   - workspace  the workspace row {id, label, company} (null = closed)
 *   - onOpenChange(bool)
 */
export default function WorkspaceTearSheetDialog({ workspace, onOpenChange }) {
  const open = !!workspace;
  const [sections, setSections] = useState({
    cover: true, summary: true, properties: true, tenancy: true, contact: true,
  });
  const [downloading, setDownloading] = useState(false);

  // Share form
  const [label, setLabel] = useState("");
  const [password, setPassword] = useState("");
  const [expiresIn, setExpiresIn] = useState(30);
  const [notifyOnView, setNotifyOnView] = useState(true);
  const [creating, setCreating] = useState(false);

  // Existing links
  const [links, setLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(false);

  useEffect(() => {
    if (!open || !workspace?.id) return;
    setSections({ cover: true, summary: true, properties: true, tenancy: true, contact: true });
    setLabel(""); setPassword(""); setExpiresIn(30); setNotifyOnView(true);
    loadLinks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, workspace?.id]);

  const loadLinks = async () => {
    if (!workspace?.id) return;
    setLoadingLinks(true);
    try {
      const { data } = await api.get(`/workspaces/${workspace.id}/tear-sheet/links`);
      setLinks(data.items || []);
    } catch {
      setLinks([]);
    } finally {
      setLoadingLinks(false);
    }
  };

  const toggle = (key) => setSections((s) => ({ ...s, [key]: !s[key] }));
  const anySection = Object.values(sections).some(Boolean);

  const downloadPdf = async () => {
    if (!anySection) {
      toast.error("Pick at least one section to include");
      return;
    }
    setDownloading(true);
    try {
      const res = await api.post(
        `/workspaces/${workspace.id}/tear-sheet`,
        { sections },
        { responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeLabel = (workspace.label || "workspace").replace(/\s+/g, "_");
      a.download = `${safeLabel}_tear_sheet.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      toast.success("Tear sheet downloaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const createShareLink = async () => {
    if (!anySection) {
      toast.error("Pick at least one section to include");
      return;
    }
    setCreating(true);
    try {
      const payload = {
        sections,
        label: label.trim() || null,
        password: password.trim() || null,
        expires_in_days: Number(expiresIn) || 30,
        notify_on_view: notifyOnView,
      };
      const { data } = await api.post(
        `/workspaces/${workspace.id}/tear-sheet/share`,
        payload,
      );
      toast.success("Share link created");
      await loadLinks();
      setLabel(""); setPassword("");
      if (data.public_url) {
        try {
          await navigator.clipboard.writeText(data.public_url);
          toast.message("Link copied to clipboard");
        } catch { /* clipboard may be unavailable; silent */ }
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to create link");
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async (link) => {
    const origin = window.location.origin;
    const url = `${origin}/share/workspace-tearsheet/${link.token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Clipboard unavailable — copy manually from the URL field");
      window.prompt("Copy link:", url);
    }
  };

  const revokeLink = async (link) => {
    if (!window.confirm(`Revoke link "${link.label || link.token.slice(0, 8)}"? It can no longer be opened.`)) return;
    try {
      await api.delete(`/workspaces/${workspace.id}/tear-sheet/links/${link.id}`);
      await loadLinks();
      toast.success("Link revoked");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Revoke failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="workspace-tearsheet-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-600" /> Tear Sheet
          </DialogTitle>
          {workspace && (
            <p className="text-sm text-slate-500 mt-1">
              One-page portfolio summary for{" "}
              <span className="font-semibold text-slate-700">{workspace.label}</span>
              {workspace.company && <span className="text-slate-500"> · {workspace.company}</span>}
              . Share with lenders, partners, or investors.
            </p>
          )}
        </DialogHeader>

        {/* Sections */}
        <div className="mt-3">
          <div className="overline text-slate-600 mb-2">Include sections</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {[
              { key: "cover",      label: "Cover" },
              { key: "summary",    label: "Portfolio summary" },
              { key: "properties", label: "Properties" },
              { key: "tenancy",    label: "Tenancy" },
              { key: "contact",    label: "Contact" },
            ].map((s) => (
              <label
                key={s.key}
                data-testid={`tearsheet-section-${s.key}`}
                className={`cursor-pointer flex items-center gap-2 px-3 py-2 rounded-sm border text-xs font-mono uppercase tracking-widest transition-colors ${
                  sections[s.key]
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100"
                }`}
              >
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={sections[s.key]}
                  onChange={() => toggle(s.key)}
                />
                {sections[s.key] && <CheckCircle2 className="w-3.5 h-3.5" />}
                {s.label}
              </label>
            ))}
          </div>
        </div>

        {/* Quick download */}
        <div className="mt-4 border-t border-slate-200 pt-4">
          <button
            type="button"
            onClick={downloadPdf}
            disabled={downloading || !anySection}
            data-testid="tearsheet-download-pdf-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            {downloading ? "Generating…" : "Download PDF"}
          </button>
          <span className="ml-3 text-xs text-slate-500">Private — no share link is created.</span>
        </div>

        {/* Share link builder */}
        <div className="mt-6 border-t border-slate-200 pt-4">
          <div className="overline text-slate-600 mb-2 flex items-center gap-2">
            <Link2 className="w-4 h-4" /> Create share link
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Label (e.g. JP Morgan)">
              <input
                className="input-clean w-full"
                data-testid="tearsheet-share-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Optional"
                maxLength={100}
              />
            </Field>
            <Field label="Password (optional)">
              <input
                type="text"
                className="input-clean w-full"
                data-testid="tearsheet-share-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="4-64 chars"
                minLength={4}
                maxLength={64}
              />
            </Field>
            <Field label="Expires in (days)">
              <input
                type="number"
                className="input-clean w-full"
                data-testid="tearsheet-share-expires"
                value={expiresIn}
                onChange={(e) => setExpiresIn(e.target.value)}
                min={1}
                max={365}
              />
            </Field>
          </div>
          <label className="mt-3 inline-flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={notifyOnView}
              onChange={(e) => setNotifyOnView(e.target.checked)}
              data-testid="tearsheet-share-notify"
            />
            Email me when this link is viewed
          </label>
          <div className="mt-3">
            <button
              type="button"
              onClick={createShareLink}
              disabled={creating || !anySection}
              data-testid="tearsheet-create-share-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50"
            >
              <Link2 className="w-4 h-4" />
              {creating ? "Creating…" : "Create share link"}
            </button>
          </div>
        </div>

        {/* Existing links */}
        <div className="mt-6 border-t border-slate-200 pt-4">
          <div className="overline text-slate-600 mb-2">Active share links</div>
          {loadingLinks ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : links.length === 0 ? (
            <div className="text-sm text-slate-500 italic">No share links yet.</div>
          ) : (
            <div className="space-y-2" data-testid="tearsheet-links-list">
              {links.map((l) => (
                <div
                  key={l.id}
                  className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 rounded-sm p-3"
                  data-testid={`tearsheet-link-${l.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                      {l.label || <span className="italic text-slate-500">Untitled</span>}
                      {l.password_protected && (
                        <Lock className="w-3 h-3 text-amber-600" title="Password-protected" />
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 font-mono flex items-center gap-3 mt-0.5">
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {l.expires_at ? `Expires ${fmtUSDate(l.expires_at)}` : "No expiry"}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Eye className="w-3 h-3" /> {l.view_count || 0} view{l.view_count === 1 ? "" : "s"}
                      </span>
                      {l.last_viewed_at && (
                        <span>Last viewed {fmtUSDate(l.last_viewed_at)}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => copyLink(l)}
                      className="p-2 rounded text-slate-500 hover:text-indigo-700 hover:bg-indigo-50"
                      title="Copy link to clipboard"
                      data-testid={`tearsheet-copy-${l.id}`}
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => revokeLink(l)}
                      className="p-2 rounded text-slate-500 hover:text-rose-700 hover:bg-rose-50"
                      title="Revoke link"
                      data-testid={`tearsheet-revoke-${l.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="btn-outline-dark"
          >
            Close
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="overline text-slate-600 mb-1">{label}</div>
      {children}
    </label>
  );
}
